// api.ts

import { auth } from "@/firebase";
import { useAuthStore } from "@/store/useAuthStore";
import axios, { AxiosError, type AxiosInstance, type InternalAxiosRequestConfig } from "axios";
import { signOut } from "firebase/auth";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "https://compliancehk.com/api/v1";

const api: AxiosInstance = axios.create({
  baseURL: API_BASE_URL,
  timeout: 10000,
  headers: {
    "Content-Type": "application/json",
  },
  withCredentials: false,
});

// Variables to handle parallel request queueing during Firebase token force-refresh
let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string) => void;
  reject: (error: unknown) => void;
}> = [];

const processQueue = (error: unknown, token: string | null = null) => {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else if (token) {
      promise.resolve(token);
    }
  });
  failedQueue = [];
};

/**
 * Handles clearing local state, Zustand store, Firebase auth session, and redirecting to login.
 */
const handleLogoutAndRedirect = async (message = "Session expired. Please log in again.") => {
  // Clear Firebase auth session
  try {
    await signOut(auth);
  } catch (err) {
    console.error("Firebase signout error:", err);
  }

  // Clear legacy tokens if any exist
  localStorage.removeItem("accessToken");
  localStorage.removeItem("refreshToken");
  localStorage.removeItem("user");

  // Reset Zustand state (removed profile: null)
  useAuthStore.setState({ user: null, isAuthenticated: false, isLoading: false });

  // Store toast notification for retrieval on the Login component
  sessionStorage.setItem("auth_toast_error", message);

  // Preserve location path for seamless redirect back after login
  const currentPath = window.location.pathname + window.location.search;
  const redirectUrl = currentPath && currentPath !== "/login" ? `/login?redirect=${encodeURIComponent(currentPath)}` : "/login";

  window.location.href = redirectUrl;
};
api.interceptors.request.use(
  async (config) => {
    const firebaseUser = auth.currentUser;

    if (firebaseUser && config.headers) {
      // 1. Fetch fresh JWT ID token directly from Firebase Auth session
      const token = await firebaseUser.getIdToken();
      // console.log(`Bearer ${token}`);
      // 2. Attach Bearer token required by NestJS Passport JwtStrategy
      config.headers.Authorization = `Bearer ${token}`;
    }

    return config;
  },
  (error) => Promise.reject(error),
);

// Response Interceptor
api.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & { _retry?: boolean };

    // Handle 401 Unauthorized status from NestJS backend
    if (error.response?.status === 401) {
      // Scenario 1: Retried request failed with 401 again -> Force logout
      if (originalRequest._retry) {
        await handleLogoutAndRedirect("Session invalid or expired. Please log in again.");
        return Promise.reject(error);
      }

      // Scenario 2: Refresh is already in progress -> Queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            if (originalRequest.headers) {
              originalRequest.headers.Authorization = `Bearer ${token}`;
            }
            return api(originalRequest);
          })
          .catch((err) => Promise.reject(err));
      }

      // Scenario 3: First 401 encounter -> Attempt Firebase token force refresh
      originalRequest._retry = true;
      isRefreshing = true;

      const user = auth.currentUser;

      if (!user) {
        isRefreshing = false;
        await handleLogoutAndRedirect("Session expired. Please log in again.");
        return Promise.reject(error);
      }

      try {
        // Force refresh Firebase ID token
        const freshToken = await user.getIdToken(true);

        if (originalRequest.headers) {
          originalRequest.headers.Authorization = `Bearer ${freshToken}`;
        }

        // Resolve queued requests
        processQueue(null, freshToken);

        // Retry original request
        return api(originalRequest);
      } catch (refreshError) {
        // Force refresh failed -> Reject queued requests and clear session
        processQueue(refreshError, null);
        await handleLogoutAndRedirect("Session expired. Please log in again.");
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    return Promise.reject(error);
  },
);

export default api;
